import React, { useState } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import {
  LayoutDashboard, Users, FileText, LogOut, Menu, X,
  Tag, BarChart3, Store, Shield, UserCog, Building,
  Package, MessageCircle, PieChart, ChevronRight, Bell, Percent, Truck, Star, LineChart, CalendarDays, Banknote, CalendarCheck, Navigation, Gauge
} from 'lucide-react';
import NotificationBell from '@/components/NotificationBell';
import AdminBottomNav from '@/components/AdminBottomNav';

const IndianRupeeIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M6 3h12M6 8h12M6 13l8.5-8M6 13h5.5a4 4 0 110 8H6" />
  </svg>
);

// ── Nav item ────────────────────────────────────────────────────────────────
const NavItem = ({ item, isActive, onClick }) => {
  const Icon = item.icon;
  return (
    <Link
      to={item.path}
      data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
      onClick={onClick}
      className={`group flex items-center gap-3 px-3 py-2.5 rounded-xl font-medium text-sm transition-all duration-200 ${
        isActive
          ? 'bg-blue-600 text-white shadow-md shadow-blue-200'
          : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
      }`}
    >
      <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors ${
        isActive ? 'bg-white/20' : 'bg-slate-100 group-hover:bg-white group-hover:shadow-sm'
      }`}>
        <Icon className={`w-4 h-4 ${isActive ? 'text-white' : 'text-slate-500 group-hover:text-blue-600'}`} />
      </div>
      <span className="truncate">{item.label}</span>
      {isActive && <ChevronRight className="w-3.5 h-3.5 ml-auto text-white/70 flex-shrink-0" />}
    </Link>
  );
};

// ── Section label ───────────────────────────────────────────────────────────
const SectionLabel = ({ label }) => (
  <p className="px-3 pt-5 pb-2 text-[10px] font-black text-slate-400 uppercase tracking-widest">
    {label}
  </p>
);

// ── Role badge ───────────────────────────────────────────────────────────────
const roleMeta = {
  super_admin:  { label: 'Super Admin',  cls: 'bg-orange-100 text-orange-700 border-orange-200' },
  store_admin:  { label: 'Store Admin',  cls: 'bg-blue-100 text-blue-700 border-blue-200' },
  store_staff:  { label: 'Staff',        cls: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
};

const DashboardLayout = () => {
  const { user, logout, isSuperAdmin } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = () => { logout(); navigate('/login'); };

  const baseMenuItems = [
    { path: '/dashboard',          label: 'Dashboard',       icon: LayoutDashboard },
    { path: '/dashboard/customers',label: 'Customers',       icon: Users },
    { path: '/dashboard/tickets',  label: 'Tickets',         icon: FileText },
    { path: '/dashboard/rates',    label: 'Rate Management', icon: Tag },
    { path: '/dashboard/billing',  label: 'Billing History', icon: IndianRupeeIcon },
    { path: '/dashboard/analytics',label: 'Analytics',       icon: BarChart3 },
  ];

  const pgMenuItems = [
    { path: '/dashboard/admin/orders', label: 'PG Orders',   icon: Package },
    { path: '/dashboard/admin/pgs',    label: 'Manage PGs',  icon: Building },
  ];

  const individualMenuItems = [
    { path: '/dashboard/admin/individual-customers', label: 'Individual Customers', icon: Users },
    { path: '/dashboard/admin/individual-orders',    label: 'Individual Orders',    icon: Package },
    { path: '/dashboard/admin/slot-management',      label: 'Slot Management',      icon: CalendarDays },
    { path: '/dashboard/admin/coupons',              label: 'Coupons',              icon: Percent },
    { path: '/dashboard/admin/ratings',              label: 'Ratings & Feedback',   icon: Star },
    { path: '/dashboard/admin/individual-analytics', label: 'Revenue Analytics',    icon: LineChart },
    { path: '/dashboard/admin/price-dashboard',      label: 'Price Dashboard',      icon: Banknote },
  ];

  const opsMenuItems = [
    { path: '/dashboard/admin',                label: 'Admin Overview',   icon: Shield },
    { path: '/dashboard/admin/daily-orders',   label: 'Daily Orders',     icon: CalendarCheck },
    { path: '/dashboard/admin/delivery-tracking',  label: 'Delivery Tracking',  icon: Navigation },
    { path: '/dashboard/admin/delivery-performance', label: 'Delivery Performance', icon: Gauge },
    { path: '/dashboard/admin/stores',         label: 'Manage Stores',    icon: Store },
    { path: '/dashboard/admin/users',          label: 'Manage Users',     icon: UserCog },
    { path: '/dashboard/admin/chats',          label: 'Customer Support', icon: MessageCircle },
    { path: '/dashboard/admin/analytics',      label: 'PG Analytics',     icon: PieChart },
    { path: '/dashboard/admin/delivery-boys',  label: 'Delivery Boys',    icon: Truck },
  ];

  // For getPageTitle — flat list of all admin items
  const allAdminItems = [...pgMenuItems, ...individualMenuItems, ...opsMenuItems];

  const isActive = (path) =>
    location.pathname === path ||
    (path !== '/dashboard' && location.pathname.startsWith(path));

  const getPageTitle = () => {
    const all = [...allAdminItems, ...baseMenuItems];
    const match = all.find(i => isActive(i.path));
    if (match) return match.label;
    if (location.pathname.startsWith('/dashboard/tickets/')) return 'Ticket Details';
    return 'Dashboard';
  };

  const role = user?.role;
  const badge = roleMeta[role] || { label: role, cls: 'bg-slate-100 text-slate-600 border-slate-200' };

  const closeSidebar = () => setSidebarOpen(false);

  const SidebarContent = () => (
    <div className="flex flex-col h-full bg-white border-r border-slate-200">
      {/* Logo */}
      <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
        <button onClick={() => navigate('/')} className="hover:opacity-80 transition-opacity">
          <img src="/logo.png" alt="JustFold" className="h-9 w-auto object-contain" />
        </button>
        <button onClick={closeSidebar} className="lg:hidden w-7 h-7 flex items-center justify-center rounded-lg text-slate-400 hover:bg-slate-100 transition-colors">
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Nav */}
      <nav className="flex-1 px-3 py-3 overflow-y-auto space-y-0.5">
        {isSuperAdmin() && (
          <>
            <SectionLabel label="PG Management" />
            {pgMenuItems.map(item => (
              <NavItem key={item.path} item={item} isActive={isActive(item.path)} onClick={closeSidebar} />
            ))}
            <SectionLabel label="Individual Customers" />
            {individualMenuItems.map(item => (
              <NavItem key={item.path} item={item} isActive={isActive(item.path)} onClick={closeSidebar} />
            ))}
            <SectionLabel label="Operations Hub" />
            {opsMenuItems.map(item => (
              <NavItem key={item.path} item={item} isActive={isActive(item.path)} onClick={closeSidebar} />
            ))}
            <div className="my-2 border-t border-slate-100" />
            <SectionLabel label="Store Operations" />
          </>
        )}
        {baseMenuItems.map(item => (
          <NavItem key={item.path} item={item} isActive={isActive(item.path)} onClick={closeSidebar} />
        ))}
      </nav>

      {/* User card */}
      <div className="px-3 py-4 border-t border-slate-100">
        <div className="bg-slate-50 rounded-2xl p-3 mb-2.5 border border-slate-100">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-blue-600 flex items-center justify-center text-white font-bold text-sm flex-shrink-0 shadow-sm shadow-blue-200">
              {user?.name?.charAt(0).toUpperCase() || 'A'}
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-bold text-slate-900 truncate">{user?.name}</p>
              <p className="text-xs text-slate-400 truncate">{user?.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-2 mt-2.5">
            <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${badge.cls}`}>
              {badge.label}
            </span>
            {user?.store_name && (
              <span className="text-[10px] text-orange-600 font-semibold flex items-center gap-1 bg-orange-50 px-2 py-0.5 rounded-full border border-orange-100">
                <Store className="w-2.5 h-2.5" />{user.store_name}
              </span>
            )}
          </div>
        </div>
        <button
          onClick={handleLogout}
          data-testid="logout-btn"
          className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-semibold text-red-500 hover:bg-red-50 border border-red-100 hover:border-red-200 transition-all"
        >
          <LogOut className="w-4 h-4" /> Logout
        </button>
      </div>
    </div>
  );

  return (
    <div className="flex h-screen bg-slate-50 overflow-hidden">

      {/* ── Sidebar desktop ── */}
      <aside className="hidden lg:flex w-64 flex-shrink-0">
        <SidebarContent />
      </aside>

      {/* ── Sidebar mobile (slide-over) ── */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-50 lg:hidden flex">
          <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm" onClick={closeSidebar} />
          <aside className="relative w-64 flex-shrink-0 z-10">
            <SidebarContent />
          </aside>
        </div>
      )}

      {/* ── Main ── */}
      <div className="flex-1 flex flex-col overflow-hidden">

        {/* Top bar */}
        <header className="bg-white border-b border-slate-200 px-4 sm:px-6 py-3 flex items-center justify-between flex-shrink-0 z-10">
          <div className="flex items-center gap-3 min-w-0">
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden w-9 h-9 flex items-center justify-center rounded-xl text-slate-500 hover:bg-slate-100 transition-colors"
              data-testid="open-sidebar-btn"
            >
              <Menu className="w-5 h-5" />
            </button>
            <div>
              <h2 className="text-lg font-black text-slate-900 truncate leading-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {getPageTitle()}
              </h2>
              <p className="text-xs text-slate-400 hidden sm:block">
                {new Date().toLocaleDateString('en-IN', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 sm:gap-3">
            {/* Notification bell */}
            <NotificationBell />

            {/* Welcome pill */}
            <div className="hidden md:flex items-center gap-2 bg-blue-50 border border-blue-100 rounded-xl px-3 py-1.5">
              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
              <span className="text-xs font-semibold text-slate-700">
                {user?.name?.split(' ')[0] || 'Admin'}
              </span>
            </div>

            {/* Logout — mobile only */}
            <button
              onClick={handleLogout}
              className="lg:hidden w-9 h-9 flex items-center justify-center rounded-xl text-red-500 hover:bg-red-50 border border-red-100 transition-colors"
              title="Logout"
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-y-auto bg-slate-50 p-4 sm:p-6 pb-20 lg:pb-6">
          <Outlet />
        </main>
      </div>

      {/* Mobile bottom nav */}
      <AdminBottomNav onMoreClick={() => setSidebarOpen(true)} />
    </div>
  );
};

export default DashboardLayout;
